import { createAction, createStoreAction } from '@cobuildlab/react-simple-state';
import axios from 'axios';
import {
  fetchPreviousSubscriptionCountStore,
  fetchSubscriptionActiveByMonthsErrorEvents,
  fetchSubscriptionActiveByMonthsEvents,
  fetchSubscriptionActiveTotalErrorEvents,
  fetchSubscriptionActiveTotalEvents,
  fetchSubscriptionByMonthsErrorEvents,
  fetchSubscriptionByMonthsEvents,
  fetchSubscriptionByPaymentMethodErrorEvents,
  fetchSubscriptionByPaymentMethodEvents,
  fetchSubscriptionCountErrorEvent,
  fetchSubscriptionCountEvent,
  fetchSubscriptionCountStore,
  fetchSubscriptionRenewCountEvent,
  fetchSubscriptionsByPriceEvent,
  fetchSubscriptionsByPriceEventError,
  fetchSubscriptionsErrorEvent,
  fetchSubscriptionsEvent,
  fetchSubscriptionsNextExpireByMonthEvent,
  fetchSubscriptionsNextExpireByMonthEventError,
  fetchSubscriptionsRecurrentErrorEvent,
  fetchSubscriptionsRecurrentEvent,
  fetchSubscriptionsReportErrorEvent,
  fetchSubscriptionsReportEvent,
  fetchTotalSubscriptionsReportErrorEvent,
  fetchTotalSubscriptionsReportEvent,
} from './subscription-events';
import { BACKEND_ENDPOINT, DateFilter, MONTHS } from '../../shared/constants';
import { CountData } from '../dashboard/dashboard-types';
import {
  GlobalSubscriptionUser,
  SubscriptionMetric,
  SubscriptionsByPrice,
  SubscriptionsEvent,
} from './subscription-types';
import {
  subscriptionNextExpireTableFilterStore,
  subscriptionTableFilterStore,
} from './subscription-store';
import { SortTableInput, User } from '../../shared/types/generated';
import { handlePreviousDateRange } from './subscription-utils';

export const fetchSubscriptionCount = createAction(
  fetchSubscriptionCountEvent,
  fetchSubscriptionCountErrorEvent,
  async () => {
    const response = await axios.get<{ count: number }>(
      `${BACKEND_ENDPOINT}/api/user/subscription/count`,
    );
    return {
      count: response.data.count,
    };
  },
);

export const fetchSubscriptionByMonth = createAction(
  fetchSubscriptionByMonthsEvents,
  fetchSubscriptionByMonthsErrorEvents,
  async (data: { from: string; to: string; filter: DateFilter }) => {
    const { from, to, filter } = data;

    const response = await axios.get<{ count: number; date: string }[]>(
      `${BACKEND_ENDPOINT}/api/user/subscription/count-by-date`,
      {
        params: {
          to,
          from,
          filter,
        },
      },
    );

    const previousRange = handlePreviousDateRange(from, to);
    
    const responsePrevious = await axios.get<{ count: number; date: string }[]>(
      `${BACKEND_ENDPOINT}/api/user/subscription/count-by-date`,
      {
        params: {
          to: previousRange.to,
          from: previousRange.from,
          filter,
        },
      },
    );

    return {
      data: response.data,
      previousData: responsePrevious.data,
    };
  },
);

export const fetchSubscriptionsCountStoreAction = createStoreAction(
  fetchSubscriptionCountStore,
  (prev, loading: boolean, subscriptions: CountData[]) => ({
    ...prev,
    loading,
    subscriptions,
  }),
);

export const fetchPreviousSubscriptionsCountStoreAction = createStoreAction(
  fetchPreviousSubscriptionCountStore,
  (prev, loading: boolean, subscriptions: CountData[]) => ({
    ...prev,
    loading,
    subscriptions,
  }),
);

export const fetchSubscriptionByPaymentMethod = async (data: {
  from: string;
  to: string;
}): Promise<SubscriptionMetric[]> => {
  const { to, from } = data;
  const response = await axios.get<SubscriptionMetric[]>(
    `${BACKEND_ENDPOINT}/api/user/subscription/payment-method-count`,
    {
      params: {
        to,
        from,
      },
    },
  );

  return response.data;
};

export const fetchSubscriptionByPaymentMethodMetrics = createAction(
  fetchSubscriptionByPaymentMethodEvents,
  fetchSubscriptionByPaymentMethodErrorEvents,
  fetchSubscriptionByPaymentMethod,
);

export const fetchUserSubscriptions = createAction(
  fetchSubscriptionsEvent,
  fetchSubscriptionsErrorEvent,
  async (data: {
    from: string;
    to: string;
    page: number;
    status: string;
    paymentMethod: string;
    orderBy?: SortTableInput;
    paymentType?: 'PAYMENT' | 'RECURRENT_PAYMENT';
  }) => {
    const { to, from, page, status, paymentMethod, orderBy } = data;

    const response = await axios.get<SubscriptionsEvent>(
      `${BACKEND_ENDPOINT}/api/user/subscription/all-by-filters`,
      {
        timeout: 60000,
        params: {
          to,
          from,
          page,
          status,
          paymentMethod,
          orderByField: orderBy?.field,
          orderByOrder: orderBy?.order,
          paymentType: data?.paymentType,
        },
      },
    );
    return response.data;
  },
);
export const fetchUserSubscriptionsRecurrent = createAction(
  fetchSubscriptionsRecurrentEvent,
  fetchSubscriptionsRecurrentErrorEvent,
  async (data: {
    from: string;
    to: string;
    page: number;
    status: string;
    paymentMethod: string;
    orderBy?: SortTableInput;
    paymentType?: 'PAYMENT' | 'RECURRENT_PAYMENT';
  }) => {
    const { to, from, page, status, paymentMethod, orderBy } = data;

    const response = await axios.get<SubscriptionsEvent>(
      `${BACKEND_ENDPOINT}/api/user/subscription/all-by-filters`,
      {
        timeout: 60000,
        params: {
          to,
          from,
          page,
          status,
          paymentMethod,
          orderByField: orderBy?.field,
          orderByOrder: orderBy?.order,
          paymentType: data?.paymentType,
        },
      },
    );
    return response.data;
  },
);

export const fetchGenerateSubscriptionUserReport = createAction(
  fetchSubscriptionsReportEvent,
  fetchSubscriptionsReportErrorEvent,
  async (data: {
    user: User | null | undefined;
    from?: string;
    to?: string;
    paymentType?: 'PAYMENT' | 'RECURRENT_PAYMENT';
  }) => {
    const { to, from } = data;

    const response = await axios.get<GlobalSubscriptionUser[]>(
      `${BACKEND_ENDPOINT}/api/user/subscription/generate-subscription-report`,
      {
        params: {
          requested_by: data.user?.name ?? data.user?.email,
          to,
          from,
          paymentType: data?.paymentType
        },
      },
    );

    return {
      report: response.data,
    };
  },
);

export const fetchGenerateTotalSubscriptionUserReport = createAction(
  fetchTotalSubscriptionsReportEvent,
  fetchTotalSubscriptionsReportErrorEvent,
  async (data: { user: User | null | undefined; from?: string; to?: string }) => {
    const { to, from } = data;

    const response = await axios.get<GlobalSubscriptionUser[]>(
      `${BACKEND_ENDPOINT}/api/user/subscription/generate-subscription-report`,
      {
        params: {
          requested_by: data.user?.name ?? data.user?.email,
          to,
          from,
        },
      },
    );

    return {
      report: response.data,
    };
  },
);

export const fetchSubscriptionRenewCount = createAction(
  fetchSubscriptionRenewCountEvent,
  fetchSubscriptionRenewCountEvent,
  async (data: { from: string; to: string; filter: DateFilter }) => {
    const { from, to, filter } = data;
    const response = await axios.get<{ count: number; date: string }[]>(
      `${BACKEND_ENDPOINT}/api/user/subscription/renew/count`,
      {
        params: {
          to,
          from,
          filter,
        },
      },
    );

    return response.data;
  },
);

export const fetchSubscriptionActiveByMonth = createAction(
  fetchSubscriptionActiveByMonthsEvents,
  fetchSubscriptionActiveByMonthsErrorEvents,
  async (data: { from: string; to: string; filter: DateFilter }) => {
    const { from, to, filter } = data;
    const response = await axios.get<{ count: number; date: string }[]>(
      `${BACKEND_ENDPOINT}/api/user/subscription/count-by-date`,
      {
        params: {
          to,
          from,
          filter,
          status: 'ACTIVE',
        },
      },
    );

    return response.data;
  },
);
export const fetchSubscriptionActiveTotal = createAction(
  fetchSubscriptionActiveTotalEvents,
  fetchSubscriptionActiveTotalErrorEvents,
  async () => {
    const response = await axios.get<{ count: number }>(
      `${BACKEND_ENDPOINT}/api/user/subscription/count`,
      {
        params: {
          status: 'ACTIVE',
        },
      },
    );
    return response.data;
  },
);

export const subscriptionTableFilterStoreAction = createStoreAction(
  subscriptionTableFilterStore,
  (prev, status: string, paymentMethod: string) => ({
    ...prev,
    status,
    paymentMethod,
  }),
);

export const subscriptionNextExpireTableFilterStoreAction = createStoreAction(
  subscriptionNextExpireTableFilterStore,
  (prev, monthExpire: MONTHS, status: string, paymentMethod: string) => ({
    ...prev,
    monthExpire,
    status,
    paymentMethod,
  }),
);

export const fetchSubscriptionsByPrice = createAction(
  fetchSubscriptionsByPriceEvent,
  fetchSubscriptionsByPriceEventError,
  async (data: { from: string; to: string; status: string; paymentMethod: string }) => {
    const { to, from, status, paymentMethod } = data;

    const response = await axios.get<SubscriptionsByPrice>(
      `${BACKEND_ENDPOINT}/api/user/subscription/all-by-price`,
      {
        timeout: 60000,
        params: {
          to,
          from,
          status,
          paymentMethod,
        },
      },
    );

    return response.data;
  },
);

export const fetchSubscriptionsNextExpireByMonth = createAction(
  fetchSubscriptionsNextExpireByMonthEvent,
  fetchSubscriptionsNextExpireByMonthEventError,
  async (data: {
    page: number;
    monthExpire: MONTHS;
    status: string;
    paymentMethod: string;
    orderBy?: SortTableInput;
  }) => {
    const { page, monthExpire, status, paymentMethod, orderBy } = data;

    const response = await axios.get<SubscriptionsEvent>(
      `${BACKEND_ENDPOINT}/api/user/subscription/next-expire-by-month`,
      {
        timeout: 60000,
        params: {
          page,
          monthExpire,
          status,
          paymentMethod,
          orderByField: orderBy?.field,
          orderByOrder: orderBy?.order,
        },
      },
    );
    return response.data;
  },
);
